import { Show, useRecordContext, useRefresh, useNotify } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Grid, Chip, Divider, Alert, Button, Stack } from '@mui/material';
import { CheckCircle, Warning, Schedule, Cancel, SwapHoriz, Receipt } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { useEntitlement } from '@/components/domain-engine/useEntitlement';
import { DEFAULT_TENANT_ID } from '@/components/domain-engine/TenantSelector';
import { fetchApi } from '@/api/fetchApi';
import type { TransactionResponse } from '@rosta/data-model/application-agnostic/ledger/dtos';

/**
 * Transaction detail — lines, status banner, and the Posted-only reverse
 * action (POST /transactions/:id/reverse, sprint 03 endpoint).
 *
 * @see Documentation/Specification/04_Processes/application-agnostic/ledger/JournalPosting.md
 * @see sprints/2026-10/Milestone_01_CompleteLedgerImplementation/sprint-07-reporting-period-close/README.md
 */

interface TransactionRecord extends TransactionResponse {
  id: string;
}

const STATUS_BANNER = {
  Posted: { severity: 'success' as const, icon: <CheckCircle />, msg: 'Transaction posted and committed' },
  Pending: { severity: 'info' as const, icon: <Schedule />, msg: 'Transaction pending — awaiting commit' },
  Voided: { severity: 'warning' as const, icon: <Cancel />, msg: 'Transaction voided' },
  Reversed: { severity: 'error' as const, icon: <Warning />, msg: 'Transaction reversed' },
};

export const TransactionShow = () => (
  <Show>
    <TransactionDetail />
  </Show>
);

const TransactionDetail = () => {
  const record = useRecordContext<TransactionRecord>();
  if (!record) return null;

  const banner = STATUS_BANNER[record.status];
  const totalDebit = record.lines?.reduce((sum, l) => sum + (l.debit ?? 0), 0) ?? 0;
  const totalCredit = record.lines?.reduce((sum, l) => sum + (l.credit ?? 0), 0) ?? 0;
  const balanced = totalDebit === totalCredit;

  return (
    <Box sx={{ p: 3 }}>
      <PageDescription
        entity="Transaction"
        description="A financial transaction record in the ledger. Tracks all monetary movements including invoices, receipts, and refunds."
        icon={<Receipt />}
      />

      {banner && <Alert severity={banner.severity} sx={{ mb: 2 }} icon={banner.icon}>{banner.msg}</Alert>}

      <ReverseTransactionAction record={record} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SwapHoriz color="primary" fontSize="large" />
        <Box>
          <Typography variant="h4" fontWeight={700}>{record.transactionNumber}</Typography>
          <Typography variant="caption" color="text.secondary">{record.transactionType} · {record.transactionDate}</Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Type</Typography>
            <Typography variant="body1" fontWeight={500} sx={{ mt: 0.5 }}>{record.transactionType}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={record.status}
                size="small"
                color={record.status === 'Posted' ? 'success' : record.status === 'Pending' ? 'warning' : record.status === 'Reversed' ? 'error' : 'default'}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Transaction Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.transactionDate}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Posting Date</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.postingDate ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.description ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Reference</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.reference ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Source Document</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.sourceDocument ?? '—'}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Journal ID</Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>{record.journalId ?? '—'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>Transaction Lines</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Chip label={`Debit: ${totalDebit.toLocaleString()}`} size="small" color="info" />
            <Chip label={`Credit: ${totalCredit.toLocaleString()}`} size="small" color="warning" />
            <Chip label={balanced ? 'Balanced' : 'Unbalanced'} size="small" color={balanced ? 'success' : 'error'} />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {record.lines?.map((line, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr 100px 100px',
                gap: 2,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" fontWeight={500}>{line.accountRef.id}</Typography>
              <Typography variant="body2">{line.description ?? '—'}</Typography>
              <Typography variant="body2" fontWeight={600} color="info.main" sx={{ textAlign: 'right' }}>
                {line.debit ? `D ${line.debit.toLocaleString()}` : '—'}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="warning.main" sx={{ textAlign: 'right' }}>
                {line.credit ? `C ${line.credit.toLocaleString()}` : '—'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * Reverse action — visible only for Posted transactions with the ledger
 * entitlement. Calls `POST /transactions/:id/reverse` with a required reason.
 */
const ReverseTransactionAction = ({ record }: { record: TransactionRecord }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { hasEntitlement } = useEntitlement(DEFAULT_TENANT_ID);
  const [busy, setBusy] = useState(false);

  const canReverse = record.status === 'Posted' && hasEntitlement('ledger');
  if (!canReverse) return null;

  const onReverse = async () => {
    const reason = window.prompt('Reversal reason:');
    if (!reason) return;
    setBusy(true);
    try {
      await fetchApi(`/transactions/${record.id}/reverse`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      notify('Transaction reversed', { type: 'success' });
      refresh();
    } catch {
      notify('Reverse failed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }} data-testid="transaction-actions">
      <Button
        variant="outlined"
        color="error"
        onClick={onReverse}
        disabled={busy}
        data-testid="reverse-button"
      >
        Reverse Transaction
      </Button>
    </Stack>
  );
};
